import { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

// ─── Rich Text Editor ────────────────────────────────────────────────────────
// Extracted from Tasks.tsx (was page-private) so the Bulletin Board announcement composer
// (Phase 2) can use the same editor Work Orders' notes/description fields already use, instead
// of duplicating ~180 lines or building a second rich-text implementation. mdToHtml is exported
// separately since Tasks.tsx also uses it standalone (read-only dangerouslySetInnerHTML render
// of saved WO description/notes/note content) independent of the editor component itself.

// Color persistence — {{color:#hex}}...{{/color}} is this editor's own markdown-ish syntax for
// text color, round-tripped through mdToHtml/htmlToMd same as **bold**/*italic*/~~strike~~.
// A prior version of this feature (Tasks.tsx, before RichTextEditor was extracted into its own
// component) added the toolbar color picker but never actually solved persistence: its htmlToMd
// regex matched a color span/font tag and threw the color away (`'$1'`, keeping only the inner
// text) instead of encoding it into the saved markdown string, so any color always evaporated on
// the very next save — a latent bug in the original feature, not something this session broke.
// This version fixes that: color is preserved through the round trip via the marker syntax below.
const COLOR_MD_RE = /\{\{color:(#[0-9a-fA-F]{3,8})\}\}([\s\S]*?)\{\{\/color\}\}/g;

function rgbToHex(rgb: string): string {
  const m = rgb.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!m) return '#f9fafb';
  const hex = (n: string) => Number(n).toString(16).padStart(2, '0');
  return `#${hex(m[1])}${hex(m[2])}${hex(m[3])}`;
}

export const mdToHtml = (md: string): string => {
  if (!md) return '';
  return md
    // Color markers first — done before bold/italic so markdown inside a colored region (e.g.
    // {{color:#f87171}}**bold**{{/color}}) still gets converted by the passes below.
    .replace(COLOR_MD_RE, '<span style="color:$1">$2</span>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/~~(.+?)~~/g, '<s>$1</s>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
};

export const htmlToMd = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
    .replace(/<b>(.*?)<\/b>/gi, '**$1**')
    .replace(/<em>(.*?)<\/em>/gi, '*$1*')
    .replace(/<i>(.*?)<\/i>/gi, '*$1*')
    .replace(/<s>(.*?)<\/s>/gi, '~~$1~~')
    .replace(/<strike>(.*?)<\/strike>/gi, '~~$1~~')
    .replace(/<ul>([\s\S]*?)<\/ul>/gi, (_, inner) =>
      inner.replace(/<li>(.*?)<\/li>/gi, '- $1\n')
    )
    .replace(/<ol>([\s\S]*?)<\/ol>/gi, (_, inner) => {
      let i = 1;
      return inner.replace(/<li>(.*?)<\/li>/gi,
        (_: string, c: string) => `${i++}. ${c}\n`);
    })
    .replace(/<li>(.*?)<\/li>/gi, '- $1\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<p>(.*?)<\/p>/gi, '$1\n\n')
    .replace(/<h[1-3]>(.*?)<\/h[1-3]>/gi, '$1\n')
    // Chrome/most browsers wrap each line in <div> (not <p>) when Enter is pressed in an empty
    // contenteditable — without this, those divs fell through to the generic tag-strip below with
    // no separator, silently concatenating every line of a multi-line body into one run-on string.
    .replace(/<div>([\s\S]*?)<\/div>/gi, '$1\n')
    // Color — execCommand('foreColor', false, hex) inserts <font color="#hex"> in Chromium
    // browsers by default; handle <span style="color:...​"> too (rgb() or hex) defensively for
    // other engines/paths. Must run BEFORE the generic tag-strip below or the color info is lost.
    .replace(/<font[^>]*color="?(#[0-9a-fA-F]{3,8})"?[^>]*>([\s\S]*?)<\/font>/gi, '{{color:$1}}$2{{/color}}')
    .replace(/<span[^>]*style="[^"]*color:\s*(#[0-9a-fA-F]{3,8}|rgb\([^)]+\))[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
      (_m, color: string, inner: string) => `{{color:${color.startsWith('#') ? color : rgbToHex(color)}}}${inner}{{/color}}`)
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
};

interface RichTextEditorProps {
  value: string;
  onChange: (md: string) => void;
  placeholder?: string;
  rows?: number;
  style?: React.CSSProperties;
}

const TOOLBAR_BUTTONS = [
  { cmd: 'bold',                icon: 'B',   title: 'Bold (Ctrl+B)',        style: { fontWeight: 700 } as React.CSSProperties },
  { cmd: 'italic',              icon: 'I',   title: 'Italic (Ctrl+I)',      style: { fontStyle: 'italic' } as React.CSSProperties },
  { cmd: 'strikeThrough',       icon: 'S̶',  title: 'Strikethrough',        style: { textDecoration: 'line-through' } as React.CSSProperties },
  { type: 'divider' } as const,
  { cmd: 'insertUnorderedList', icon: '• —', title: 'Bullet list',          style: {} as React.CSSProperties },
  { cmd: 'insertOrderedList',   icon: '1.',  title: 'Numbered list',        style: {} as React.CSSProperties },
  { type: 'divider' } as const,
  { cmd: 'removeFormat',        icon: 'Tx',  title: 'Clear formatting',     style: {} as React.CSSProperties },
  { type: 'divider' } as const,
  { type: 'color' } as const,
];

const TEXT_COLORS = [
  { color: '#f9fafb', label: 'White' },
  { color: '#f87171', label: 'Red' },
  { color: '#fb923c', label: 'Orange' },
  { color: '#facc15', label: 'Yellow' },
  { color: '#4ade80', label: 'Green' },
  { color: '#60a5fa', label: 'Blue' },
  { color: '#c084fc', label: 'Purple' },
  { color: '#f472b6', label: 'Pink' },
];

export default function RichTextEditor({ value, onChange, placeholder, rows = 3, style }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const colorBtnRef = useRef<HTMLButtonElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [colorPickerPos, setColorPickerPos] = useState<{ top: number; left: number } | null>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!editorRef.current || isInternalChange.current) return;
    const newHtml = mdToHtml(value);
    if (editorRef.current.innerHTML !== newHtml) {
      editorRef.current.innerHTML = newHtml;
    }
  }, [value]);

  const handleInput = () => {
    if (!editorRef.current) return;
    isInternalChange.current = true;
    onChange(htmlToMd(editorRef.current.innerHTML));
    setTimeout(() => { isInternalChange.current = false; }, 0);
  };

  const execCmd = (cmd: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false);
    handleInput();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b': e.preventDefault(); execCmd('bold'); break;
        case 'i': e.preventDefault(); execCmd('italic'); break;
        case 'x': if (e.shiftKey) { e.preventDefault(); execCmd('strikeThrough'); } break;
      }
    }
  };

  const minHeight = `${rows * 24}px`;

  return (
    <>
    <div style={{
      border: `1px solid ${isFocused ? 'var(--accent-primary)' : 'var(--border-default)'}`,
      borderRadius: 6,
      background: 'var(--bg-elevated)',
      overflow: 'hidden',
      transition: 'border-color 0.15s',
      ...style,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 2,
        padding: '4px 6px',
        borderBottom: '1px solid var(--border-subtle)',
        background: 'rgba(255,255,255,0.045)',
        flexWrap: 'wrap',
      }}>
        {TOOLBAR_BUTTONS.map((btn, i) => {
          if ('type' in btn && btn.type === 'divider') return (
            <div key={i} style={{ width: 1, height: 16, background: 'var(--border-default)', margin: '0 3px' }} />
          );
          if ('type' in btn && btn.type === 'color') return (
            <button
              key={i}
              ref={colorBtnRef}
              title="Text color"
              onMouseDown={e => {
                e.preventDefault();
                if (colorPickerPos) {
                  setColorPickerPos(null);
                } else {
                  const rect = colorBtnRef.current?.getBoundingClientRect();
                  if (rect) setColorPickerPos({ top: rect.top - 8, left: rect.left });
                }
              }}
              style={{
                padding: '2px 7px', fontSize: 12,
                border: 'none', borderRadius: 4,
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer', lineHeight: '20px',
                minWidth: 28, textAlign: 'center',
                display: 'flex', alignItems: 'center', gap: 3,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ fontWeight: 600, fontSize: 13 }}>A</span>
              <span style={{ width: 14, height: 3, borderRadius: 2, background: 'var(--accent-primary)', display: 'block' }} />
            </button>
          );
          const b = btn as Exclude<typeof TOOLBAR_BUTTONS[number], { type: 'divider' } | { type: 'color' }>;
          return (
            <button key={i}
              title={b.title}
              onMouseDown={e => { e.preventDefault(); execCmd(b.cmd!); }}
              style={{
                ...b.style,
                padding: '2px 7px', fontSize: 12,
                border: 'none', borderRadius: 4,
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer', lineHeight: '20px',
                minWidth: 28, textAlign: 'center',
              }}
              onMouseEnter={e => { (e.target as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.background = 'transparent'; }}
            >
              {b.icon}
            </button>
          );
        })}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => { setIsFocused(true); setColorPickerPos(null); }}
        onBlur={() => setIsFocused(false)}
        data-placeholder={placeholder ?? 'Write here...'}
        style={{
          minHeight,
          padding: '8px 10px',
          fontSize: 13,
          color: 'var(--text-primary)',
          outline: 'none',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          overflowY: 'auto',
          maxHeight: `${rows * 48}px`,
        }}
      />
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: var(--text-muted);
          pointer-events: none;
        }
      `}</style>
    </div>
    {colorPickerPos && ReactDOM.createPortal(
      <>
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 19998 }}
          onMouseDown={() => setColorPickerPos(null)}
        />
        <div style={{
          position: 'fixed',
          top: colorPickerPos.top,
          left: colorPickerPos.left,
          transform: 'translateY(-100%)',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: 8, padding: 8,
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 4, zIndex: 19999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
          {TEXT_COLORS.map(tc => (
            <button
              key={tc.color}
              title={tc.label}
              onMouseDown={e => {
                e.preventDefault();
                editorRef.current?.focus();
                document.execCommand('foreColor', false, tc.color);
                handleInput();
                setColorPickerPos(null);
              }}
              style={{
                width: 24, height: 24, borderRadius: 4,
                background: tc.color, border: 'none',
                cursor: 'pointer',
              }}
            />
          ))}
          <button
            title="Default color"
            onMouseDown={e => {
              e.preventDefault();
              editorRef.current?.focus();
              document.execCommand('foreColor', false, '#f9fafb');
              handleInput();
              setColorPickerPos(null);
            }}
            style={{
              width: 24, height: 24, borderRadius: 4,
              background: 'transparent',
              border: '1px solid var(--border-default)',
              cursor: 'pointer', fontSize: 10,
              color: 'var(--text-muted)',
            }}
          >↺</button>
        </div>
      </>,
      document.body
    )}
    </>
  );
}
